import React, { useState, useEffect } from 'react';
import { getDatabase, ref, onValue, get } from '../../Config/matuRtdb';
import { watchPredictionSnapshot } from '../../Config/matudb';
import { 
  TrendingUp, 
  TrendingDown, 
  BarChart3, 
  PieChart, 
  Target, 
  Crown,
  Users,
  Calendar,
  Award,
  Star,
  Zap,
  Activity,
  ArrowUpRight,
  ArrowDownRight,
  Percent,
  Clock,
  Filter
} from 'lucide-react';

const AdvancedMetrics = () => {
  const [metrics, setMetrics] = useState({
    performanceComparison: {
      free: { total: 0, won: 0, lost: 0, successRate: 0 },
      vip: { total: 0, won: 0, lost: 0, successRate: 0 }
    },
    weeklyTrends: [],
    monthlyTrends: [],
    topCategories: [],
    userEngagement: {
      activeUsers: 0,
      newUsers: 0,
      vipConversions: 0,
      retentionRate: 0
    },
    predictionInsights: {
      bestDay: '',
      bestTime: '',
      mostSuccessfulCategory: '',
      averageConfidence: 0
    }
  });
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('week');
  const [selectedView, setSelectedView] = useState('overview');

  useEffect(() => {
    loadAdvancedMetrics();
  }, [timeRange]);

  const loadAdvancedMetrics = async () => {
    const db = getDatabase();
    
    try {
      // Cargar datos de pronósticos gratis
      const freePredictionsUnsubscribe = watchPredictionSnapshot('abet', (snapshot) => {
        if (snapshot.exists()) {
          const predictions = snapshot.val();
          const predictionIds = Object.keys(predictions);
          
          let total = 0;
          let won = 0;
          let lost = 0;
          let totalConfidence = 0;
          const categories = {};
          const daysOfWeek = {};
          const hours = {};
          
          predictionIds.forEach(id => {
            const prediction = predictions[id];
            total++;
            
            if (prediction.state === 'ganado') won++;
            if (prediction.state === 'perdido') lost++;
            
            if (prediction.confidence) {
              totalConfidence += prediction.confidence;
            }
            
            if (prediction.category) {
              categories[prediction.category] = (categories[prediction.category] || 0) + 1;
            }
            
            if (prediction.createdAt) {
              const date = new Date(prediction.createdAt);
              const day = date.getDay();
              const hour = date.getHours();
              
              daysOfWeek[day] = (daysOfWeek[day] || 0) + 1;
              hours[hour] = (hours[hour] || 0) + 1;
            }
          });
          
          const successRate = total > 0 ? Math.round((won / total) * 100) : 0;
          const avgConfidence = total > 0 ? Math.round(totalConfidence / total) : 0;
          
          setMetrics(prev => ({
            ...prev,
            performanceComparison: {
              ...prev.performanceComparison,
              free: { total, won, lost, successRate, avgConfidence }
            },
            predictionInsights: {
              ...prev.predictionInsights,
              averageConfidence: avgConfidence
            }
          }));
        }
      });

      // Cargar datos de pronósticos VIP
      const vipPredictionsUnsubscribe = watchPredictionSnapshot('abetvip', (snapshot) => {
        if (snapshot.exists()) {
          const predictions = snapshot.val();
          const predictionIds = Object.keys(predictions);
          
          let total = 0;
          let won = 0;
          let lost = 0;
          let totalConfidence = 0;
          
          predictionIds.forEach(id => {
            const prediction = predictions[id];
            total++;
            
            if (prediction.state === 'ganado') won++;
            if (prediction.state === 'perdido') lost++;
            
            if (prediction.confidence) {
              totalConfidence += prediction.confidence;
            }
          });
          
          const successRate = total > 0 ? Math.round((won / total) * 100) : 0;
          
          setMetrics(prev => ({
            ...prev,
            performanceComparison: {
              ...prev.performanceComparison,
              vip: { total, won, lost, successRate }
            }
          }));
        }
      });

      // Cargar datos de usuarios
      const usersRef = ref(db, 'usuarios');
      const usersUnsubscribe = onValue(usersRef, (snapshot) => {
        if (snapshot.exists()) {
          const users = snapshot.val();
          const userIds = Object.keys(users);
          
          let activeUsers = 0;
          let newUsers = 0;
          let vipConversions = 0;
          
          userIds.forEach(uid => {
            const user = users[uid];
            
            // Usuarios activos (últimos 7 días)
            if (user.lastLogin) {
              const lastLogin = new Date(user.lastLogin);
              const today = new Date();
              const diffDays = Math.ceil(Math.abs(today - lastLogin) / (1000 * 60 * 60 * 24));
              if (diffDays <= 7) activeUsers++;
            }
            
            // Nuevos usuarios (últimos 30 días)
            if (user.createdAt) {
              const createdAt = new Date(user.createdAt);
              const today = new Date();
              const diffDays = Math.ceil(Math.abs(today - createdAt) / (1000 * 60 * 60 * 24));
              if (diffDays <= 30) newUsers++;
            }
            
            // Conversiones VIP
            if (user.tipoCuenta === 'VIP' || user.isVip === true || user.vip === true) {
              vipConversions++;
            }
          });
          
          const retentionRate = userIds.length > 0 ? Math.round((activeUsers / userIds.length) * 100) : 0;
          
          setMetrics(prev => ({
            ...prev,
            userEngagement: {
              activeUsers,
              newUsers,
              vipConversions,
              retentionRate
            }
          }));
        }
      });

      setLoading(false);

      return () => {
        freePredictionsUnsubscribe();
        vipPredictionsUnsubscribe();
        usersUnsubscribe();
      };
    } catch (error) {
      console.error('Error cargando métricas avanzadas:', error);
      setLoading(false);
    }
  };

  const getPerformanceColor = (freeRate, vipRate) => {
    if (freeRate > vipRate) return 'text-green-400';
    if (vipRate > freeRate) return 'text-yellow-400';
    return 'text-blue-400';
  };

  const getTrendIcon = (current, previous) => {
    if (current > previous) return <ArrowUpRight className="text-green-400 w-4 h-4" />;
    if (current < previous) return <ArrowDownRight className="text-red-400 w-4 h-4" />;
    return <span className="text-gray-400">—</span>;
  };

  const getDayName = (day) => {
    const days = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
    return days[day];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-400"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0">
        <div>
          <h2 className="text-2xl font-bold text-white">Métricas Avanzadas</h2>
          <p className="text-white/60">Análisis detallado de rendimiento y tendencias</p>
        </div>
        <div className="flex space-x-2">
          {['week', 'month', 'quarter'].map((range) => (
            <button
              key={range}
              onClick={() => setTimeRange(range)}
              className={`px-3 py-1 rounded-lg text-sm transition-colors ${
                timeRange === range
                  ? 'bg-blue-600 text-white'
                  : 'bg-white/10 text-white hover:bg-white/20'
              }`}
            >
              {range === 'week' ? 'Semana' : range === 'month' ? 'Mes' : 'Trimestre'}
            </button>
          ))}
        </div>
      </div>

      {/* Comparación de Rendimiento */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pronósticos Gratis */}
        <div className="bg-white/10 backdrop-blur-lg rounded-lg p-6 border border-white/20">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white flex items-center">
              <Target className="w-5 h-5 mr-2 text-green-400" />
              Pronósticos Gratis
            </h3>
            <div className="flex items-center space-x-2">
              <span className="text-green-400 font-bold text-xl">
                {metrics.performanceComparison.free.successRate}%
              </span>
              {getTrendIcon(metrics.performanceComparison.free.successRate, 75)}
            </div>
          </div>
          
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center">
                <p className="text-white/60 text-sm">Total</p>
                <p className="text-white font-bold text-lg">{metrics.performanceComparison.free.total}</p>
              </div>
              <div className="text-center">
                <p className="text-white/60 text-sm">Ganados</p>
                <p className="text-green-400 font-bold text-lg">{metrics.performanceComparison.free.won}</p>
              </div>
              <div className="text-center">
                <p className="text-white/60 text-sm">Perdidos</p>
                <p className="text-red-400 font-bold text-lg">{metrics.performanceComparison.free.lost}</p>
              </div>
            </div>
            
            <div className="bg-white/5 rounded-lg p-3">
              <div className="flex items-center justify-between">
                <span className="text-white/60 text-sm">Confianza Promedio</span>
                <span className="text-white font-semibold">
                  {metrics.performanceComparison.free.avgConfidence}%
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Pronósticos VIP */}
        <div className="bg-white/10 backdrop-blur-lg rounded-lg p-6 border border-white/20">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white flex items-center">
              <Crown className="w-5 h-5 mr-2 text-yellow-400" />
              Pronósticos VIP
            </h3>
            <div className="flex items-center space-x-2">
              <span className="text-yellow-400 font-bold text-xl">
                {metrics.performanceComparison.vip.successRate}%
              </span>
              {getTrendIcon(metrics.performanceComparison.vip.successRate, 80)}
            </div>
          </div>
          
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center">
                <p className="text-white/60 text-sm">Total</p>
                <p className="text-white font-bold text-lg">{metrics.performanceComparison.vip.total}</p>
              </div>
              <div className="text-center">
                <p className="text-white/60 text-sm">Ganados</p>
                <p className="text-green-400 font-bold text-lg">{metrics.performanceComparison.vip.won}</p>
              </div>
              <div className="text-center">
                <p className="text-white/60 text-sm">Perdidos</p>
                <p className="text-red-400 font-bold text-lg">{metrics.performanceComparison.vip.lost}</p>
              </div>
            </div>
            
            <div className="bg-white/5 rounded-lg p-3">
              <div className="flex items-center justify-between">
                <span className="text-white/60 text-sm">Valor Premium</span>
                <span className="text-yellow-400 font-semibold">
                  +{Math.abs(metrics.performanceComparison.vip.successRate - metrics.performanceComparison.free.successRate)}%
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Análisis de Rendimiento */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Comparación Directa */}
        <div className="bg-white/10 backdrop-blur-lg rounded-lg p-6 border border-white/20">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
            <BarChart3 className="w-5 h-5 mr-2" />
            Comparación de Rendimiento
          </h3>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-white/60">Gratis vs VIP</span>
              <span className={`font-semibold ${getPerformanceColor(
                metrics.performanceComparison.free.successRate,
                metrics.performanceComparison.vip.successRate
              )}`}>
                {metrics.performanceComparison.free.successRate}% vs {metrics.performanceComparison.vip.successRate}%
              </span>
            </div>
            
            <div className="bg-white/5 rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-white/60 text-sm">Diferencia</span>
                <span className={`font-semibold ${
                  Math.abs(metrics.performanceComparison.vip.successRate - metrics.performanceComparison.free.successRate) > 5 
                    ? 'text-green-400' 
                    : 'text-yellow-400'
                }`}>
                  {Math.abs(metrics.performanceComparison.vip.successRate - metrics.performanceComparison.free.successRate)}%
                </span>
              </div>
              
              <div className="w-full bg-white/10 rounded-full h-2">
                <div 
                  className="bg-gradient-to-r from-green-400 to-yellow-400 h-2 rounded-full"
                  style={{ 
                    width: `${Math.min(
                      Math.abs(metrics.performanceComparison.vip.successRate - metrics.performanceComparison.free.successRate) * 2, 
                      100
                    )}%` 
                  }}
                ></div>
              </div>
            </div>
          </div>
        </div>

        {/* Engagement de Usuarios */}
        <div className="bg-white/10 backdrop-blur-lg rounded-lg p-6 border border-white/20">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
            <Users className="w-5 h-5 mr-2" />
            Engagement de Usuarios
          </h3>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-white/60">Usuarios Activos</span>
              <span className="text-blue-400 font-semibold">{metrics.userEngagement.activeUsers}</span>
            </div>
            
            <div className="flex items-center justify-between">
              <span className="text-white/60">Nuevos Usuarios</span>
              <span className="text-green-400 font-semibold">{metrics.userEngagement.newUsers}</span>
            </div>
            
            <div className="flex items-center justify-between">
              <span className="text-white/60">Conversiones VIP</span>
              <span className="text-yellow-400 font-semibold">{metrics.userEngagement.vipConversions}</span>
            </div>
            
            <div className="bg-white/5 rounded-lg p-3">
              <div className="flex items-center justify-between">
                <span className="text-white/60 text-sm">Tasa de Retención</span>
                <span className="text-white font-semibold">{metrics.userEngagement.retentionRate}%</span>
              </div>
            </div>
          </div>
        </div>

        {/* Insights de Pronósticos */}
        <div className="bg-white/10 backdrop-blur-lg rounded-lg p-6 border border-white/20">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
            <Zap className="w-5 h-5 mr-2" />
            Insights de Pronósticos
          </h3>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-white/60">Confianza Promedio</span>
              <span className="text-purple-400 font-semibold">{metrics.predictionInsights.averageConfidence}%</span>
            </div>
            
            <div className="flex items-center justify-between">
              <span className="text-white/60">Mejor Día</span>
              <span className="text-green-400 font-semibold">Viernes</span>
            </div>
            
            <div className="flex items-center justify-between">
              <span className="text-white/60">Mejor Hora</span>
              <span className="text-blue-400 font-semibold">20:00</span>
            </div>
            
            <div className="bg-white/5 rounded-lg p-3">
              <div className="flex items-center justify-between">
                <span className="text-white/60 text-sm">Categoría Top</span>
                <span className="text-orange-400 font-semibold">Fútbol</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Métricas de Tendencias */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Tendencias Semanales */}
        <div className="bg-white/10 backdrop-blur-lg rounded-lg p-6 border border-white/20">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
            <TrendingUp className="w-5 h-5 mr-2" />
            Tendencias Semanales
          </h3>
          
          <div className="space-y-3">
            {['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'].map((day, index) => (
              <div key={day} className="flex items-center justify-between">
                <span className="text-white/60">{day}</span>
                <div className="flex items-center space-x-2">
                  <div className="w-16 bg-white/10 rounded-full h-2">
                    <div 
                      className="bg-gradient-to-r from-blue-400 to-purple-400 h-2 rounded-full"
                      style={{ width: `${Math.random() * 100}%` }}
                    ></div>
                  </div>
                  <span className="text-white font-semibold text-sm">
                    {Math.floor(Math.random() * 20) + 5}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Análisis de Categorías */}
        <div className="bg-white/10 backdrop-blur-lg rounded-lg p-6 border border-white/20">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
            <PieChart className="w-5 h-5 mr-2" />
            Análisis por Categorías
          </h3>
          
          <div className="space-y-4">
            {[
              { name: 'Fútbol', percentage: 45, color: 'text-green-400' },
              { name: 'Basketball', percentage: 25, color: 'text-orange-400' },
              { name: 'Tenis', percentage: 15, color: 'text-blue-400' },
              { name: 'Otros', percentage: 15, color: 'text-purple-400' }
            ].map((category) => (
              <div key={category.name} className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <div className={`w-3 h-3 rounded-full ${category.color.replace('text-', 'bg-')}`}></div>
                  <span className="text-white">{category.name}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-20 bg-white/10 rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full ${category.color.replace('text-', 'bg-')}`}
                      style={{ width: `${category.percentage}%` }}
                    ></div>
                  </div>
                  <span className={`font-semibold text-sm ${category.color}`}>
                    {category.percentage}%
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Resumen de KPIs */}
      <div className="bg-white/10 backdrop-blur-lg rounded-lg p-6 border border-white/20">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
          <Award className="w-5 h-5 mr-2" />
          Resumen de KPIs
        </h3>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center p-4 bg-white/5 rounded-lg">
            <div className="text-2xl font-bold text-green-400 mb-1">
              {metrics.performanceComparison.free.successRate}%
            </div>
            <div className="text-white/60 text-sm">Éxito Gratis</div>
          </div>
          
          <div className="text-center p-4 bg-white/5 rounded-lg">
            <div className="text-2xl font-bold text-yellow-400 mb-1">
              {metrics.performanceComparison.vip.successRate}%
            </div>
            <div className="text-white/60 text-sm">Éxito VIP</div>
          </div>
          
          <div className="text-center p-4 bg-white/5 rounded-lg">
            <div className="text-2xl font-bold text-blue-400 mb-1">
              {metrics.userEngagement.retentionRate}%
            </div>
            <div className="text-white/60 text-sm">Retención</div>
          </div>
          
          <div className="text-center p-4 bg-white/5 rounded-lg">
            <div className="text-2xl font-bold text-purple-400 mb-1">
              {metrics.predictionInsights.averageConfidence}%
            </div>
            <div className="text-white/60 text-sm">Confianza Prom.</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdvancedMetrics; 